import {Box} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {Ago} from './row/Ago'
import {LABELS} from '../constants'
import type {CrossReferencedEvent$key} from './__generated__/CrossReferencedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {CrossReferencedEventInner} from './CrossReferencedEventInner'

export type ReferenceTypes = 'issues' | 'prs' | 'mixed'

type CrossReferencedEventProps = {
  queryRef: CrossReferencedEvent$key
  nextQueryRefs?: CrossReferencedEvent$key[]
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  referenceTypes?: ReferenceTypes
}

type sourceType = 'Issue' | 'PullRequest'

export const CrossReferencedEventFragment = graphql`
  fragment CrossReferencedEvent on CrossReferencedEvent {
    referencedAt
    willCloseTarget
    databaseId
    source {
      __typename
      ... on Issue {
        databaseId
        title
        url
        number
        stateReason
        repository {
          name
          owner {
            login
          }
        }
      }
      ... on PullRequest {
        issueDatabaseId
        title
        url
        number
        state
        isDraft
        isInMergeQueue
        repository {
          name
          owner {
            login
          }
        }
      }
    }
    actor {
      ...EventActor
      ...EventActorAvatarItemBadge
    }
  }
`

export function CrossReferencedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
  nextQueryRefs,
  referenceTypes,
}: CrossReferencedEventProps): JSX.Element {
  const {actor, referencedAt, willCloseTarget, source, databaseId} = useFragment(CrossReferencedEventFragment, queryRef)

  const isIssue = source.__typename === 'Issue'
  const isPullRequest = source.__typename === 'PullRequest'

  if (!isIssue && !isPullRequest) {
    return <></>
  }

  const issueDatabaseId = isPullRequest ? source.issueDatabaseId : source.databaseId

  const message = buildMessage(
    source.__typename,
    willCloseTarget,
    nextQueryRefs?.length ?? 0,
    referenceTypes ?? 'mixed',
  )
  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={referencedAt}
      actorAvatar={actor}
      showAgoTimestamp={false}
      deepLinkUrl={issueUrl}
      leadingIconOnTop={true}
    >
      <>
        {`${message} `}
        <Ago
          timestamp={new Date(referencedAt)}
          linkUrl={`${issueUrl}#ref-${isIssue ? 'issue' : 'pullrequest'}-${issueDatabaseId}`}
        />
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px'}}>
          {[queryRef, ...(nextQueryRefs ?? [])].map((itemQueryRef, index) => (
            <CrossReferencedEventInner queryRef={itemQueryRef} key={`${databaseId}_${index}`} />
          ))}
        </Box>
      </>
    </TimelineRow>
  )
}

function buildMessage(
  sourceType: sourceType,
  willCloseTarget: boolean,
  numNextItems: number,
  referenceTypes: ReferenceTypes,
) {
  if (willCloseTarget) {
    return LABELS.timeline.linkedAClosingPR
  }
  if (numNextItems === 0 || referenceTypes === 'mixed') {
    return LABELS.timeline.mentionedThisIn
  }
  if (sourceType === 'Issue') {
    return `${LABELS.timeline.mentionedThisIn} in ${numNextItems + 1} issues`
  } else {
    return `${LABELS.timeline.mentionedThisIn} in ${numNextItems + 1} pull requests`
  }
}

try{ CrossReferencedEvent.displayName ||= 'CrossReferencedEvent' } catch {}